import React, { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { signIn } from 'aws-amplify/auth';
import { useTranslation } from 'react-i18next';
import Logo from './Logo';

// Alert component
const Alert = ({ severity, children }) => {
  const styles = {
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className={`px-4 py-3 border rounded-lg ${styles[severity]}`}>
      {children}
    </div>
  );
};

const Login = ({ onLoginSuccess, onForgotPassword, onSignUp }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn({
        username: email,
        password: password,
      });

      if (result.isSignedIn || result.nextStep?.signInStep === 'DONE') {
        onLoginSuccess();
      } else if (result.nextStep) {
        console.log('Additional step required:', result.nextStep);
        setError(t('auth.error_additional_auth_required'));
      }
    } catch (err) {
      console.error('Login error:', err);
      if (err.name === 'NotAuthorizedException') {
        setError(t('auth.error_invalid_credentials'));
      } else if (err.name === 'UserNotFoundException') {
        setError(t('auth.error_user_not_found'));
      } else if (err.name === 'UserNotConfirmedException') {
        setError(t('auth.error_user_not_confirmed'));
      } else {
        setError(t('auth.error_login_failed'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm">
        <div className="card-elevated p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center">
              <Logo size={64} className="text-primary" animated />
            </div>
            <div className="flex items-center justify-center gap-3 mt-3">
              <div
                aria-label="GTÜ"
                className="bg-primary"
                style={{
                  height: 20,
                  aspectRatio: '190 / 84',
                  WebkitMaskImage: 'url(/gtue_white.svg)',
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  maskImage: 'url(/gtue_white.svg)',
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                }}
              />
              <div
                aria-label="Slalom"
                className="bg-primary"
                style={{
                  height: 11,
                  aspectRatio: '175 / 46',
                  WebkitMaskImage: 'url(/slalom_white.svg)',
                  WebkitMaskSize: 'contain',
                  WebkitMaskRepeat: 'no-repeat',
                  maskImage: 'url(/slalom_white.svg)',
                  maskSize: 'contain',
                  maskRepeat: 'no-repeat',
                }}
              />
            </div>
            <h1 className="text-2xl font-bold text-primary mt-4">
              {t('auth.welcome')}
            </h1>
            <p className="text-sm text-text-muted mt-1">
              {t('auth.sign_in_to_continue')}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-text-muted mb-1">
                {t('auth.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                disabled={loading}
                className="input"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-text-muted mb-1">
                {t('auth.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                disabled={loading}
                className="input"
              />
            </div>

            {error && (
              <Alert severity="error">{error}</Alert>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  {t('auth.signing_in')}
                </>
              ) : (
                t('auth.login')
              )}
            </button>
          </form>

          {/* Forgot Password Link */}
          <div className="text-center mt-6">
            <button
              type="button"
              onClick={onForgotPassword}
              disabled={loading}
              className="text-sm text-primary hover:underline cursor-pointer"
            >
              {t('auth.forgot_password')}
            </button>
          </div>

          {/* Sign Up Link */}
          <div className="text-center mt-4">
            <span className="text-sm text-text-muted">{t('auth.no_account')} </span>
            <button
              type="button"
              onClick={onSignUp}
              disabled={loading}
              className="text-sm text-primary hover:underline cursor-pointer"
            >
              {t('auth.sign_up')}
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-6">
            <p className="text-xs text-text-light opacity-70">
              © 2026 Slalom
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
